import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError, map, mergeMap, take,
} from 'rxjs/operators';
import {
  EMPTY, from, Observable, of,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { FsErrorResponse } from '@fairandsmart/angular';
import { FsEventType } from '@fairandsmart/types';
import { OrgaService } from '@Services/orga.service';
import { AlertService } from '@Services/alert.service';
import { CommonActionTypes, CoreEvent } from '@Common/common.actions';
import {
  LoadNews,
  LoadNewsFailure,
  LoadNewsSuccess,
  MarkNewsAsRead,
  MarkNewsAsUnread,
  NewsActionTypes,
  RefreshNews,
} from './news.actions';
import { FsOrgState } from '../../../reducers';

@Injectable()
export class NewsEffects {
  actions$ = inject(Actions);

  store = inject(Store<FsOrgState>);

  orgaService = inject(OrgaService);

  alert = inject(AlertService);

  dialog = inject(MatDialog);

  showNews = createEffect(() => this.actions$.pipe(
    ofType(NewsActionTypes.ShowNews),
    mergeMap(() => this.state$((state: FsOrgState) => {
      if (state.news.feed != null) {
        return EMPTY;
      }
      return of(new LoadNews());
    })),
  ));

  loadNews$ = createEffect(() => this.actions$.pipe(
    ofType(NewsActionTypes.LoadNews),
    mergeMap(() => this.orgaService.listNews()
      .pipe(
        map((response) => new LoadNewsSuccess({ response, newsToRead: (response.filter((news) => !news.read).length > 0) ? response.filter((news) => !news.read).length : null })),
        catchError((response: FsErrorResponse) => of(new LoadNewsFailure({ response }))),
      )),
  ));

  refreshNews = createEffect(() => this.actions$.pipe(
    ofType(NewsActionTypes.RefreshNews),
    mergeMap(() => from([
      new LoadNews(),
    ])),
  ));

  markAsRead = createEffect(() => this.actions$.pipe(
    ofType(NewsActionTypes.MarkNewsAsRead),
    mergeMap((action: MarkNewsAsRead) => this.orgaService.markNewsAsRead(action.payload.newsId)
      .pipe(
        map(() => new RefreshNews()),
        catchError((response: FsErrorResponse) => {
          this.alert.apiError(response.error, 'NEWS.MARK_READ.ERROR');
          return EMPTY;
        }),
      )),
  ));

  markAsUnread = createEffect(() => this.actions$.pipe(
    ofType(NewsActionTypes.MarkNewsAsUnread),
    mergeMap((action: MarkNewsAsUnread) => this.orgaService.markNewsAsUnRead(action.payload.newsId)
      .pipe(
        map(() => new RefreshNews()),
        catchError((response: FsErrorResponse) => {
          this.alert.apiError(response.error, 'NEWS.MARK_UNREAD.ERROR');
          return EMPTY;
        }),
      )),
  ));

  coreEvent$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.CoreEvent),
    mergeMap((action: CoreEvent) => {
      switch (action.payload.event.eventType) {
        case FsEventType.NEWS_PUBLISH:
        case FsEventType.NEWS_ARCHIVE:
        case FsEventType.NEWS_DELETE: {
          return of(new RefreshNews());
        }
        default:
          return EMPTY;
      }
    }),
  ));

  private state$(project: (state: FsOrgState) => Observable<any>): Observable<any> {
    return this.store.pipe(
      take(1),
      mergeMap(project),
    );
  }
}
